import { useEffect, useState, useCallback } from 'react';
import {
  ExternalLink,
  Bell,
  Loader2,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  Key,
  Save,
  Trash2,
  HelpCircle,
  Shield,
  Eye,
  EyeOff,
  Subtitles,
  Sparkles,
} from 'lucide-react';
import { PLATFORM_INFO } from '../types';
import type { Platform } from '../types';
import { api } from '../services/api';
import { config } from '../services/config';
import { useCaptionTemplates } from '../hooks/useApi';

interface ConnectionInfo {
  platform: string;
  status: 'connected' | 'not_connected' | 'expired';
  accountName?: string;
  connectedAt?: string;
  connectedBy?: string;
}

interface CredentialStatus {
  platform: string;
  configured: boolean;
  clientIdSet: boolean;
  clientSecretSet: boolean;
}

// Only the 6 platforms that have OAuth integrations
const OAUTH_PLATFORMS: Platform[] = ['youtube', 'linkedin', 'twitter', 'tiktok', 'instagram', 'snapchat'];

// Developer portal links + instructions per platform
const PLATFORM_DEV_INFO: Record<string, {
  devUrl: string;
  devLabel: string;
  idLabel: string;
  secretLabel: string;
  instructions: string;
}> = {
  youtube: {
    devUrl: 'https://console.cloud.google.com',
    devLabel: 'Create a project at console.cloud.google.com',
    idLabel: 'Client ID',
    secretLabel: 'Client Secret',
    instructions: 'Create an OAuth 2.0 credential in Google Cloud Console. Enable the YouTube Data API v3. Set the redirect URI to your callback URL. Required scopes: youtube.upload, youtube, youtube.readonly.',
  },
  linkedin: {
    devUrl: 'https://www.linkedin.com/developers',
    devLabel: 'Create an app at linkedin.com/developers',
    idLabel: 'Client ID',
    secretLabel: 'Client Secret',
    instructions: 'Create a LinkedIn app and request the Marketing Developer Platform product. Required scopes: openid, profile, email, w_member_social.',
  },
  twitter: {
    devUrl: 'https://developer.twitter.com',
    devLabel: 'Create an app at developer.twitter.com',
    idLabel: 'Client ID',
    secretLabel: 'Client Secret',
    instructions: 'Create a project + app in the Twitter Developer Portal. Set up OAuth 2.0 with PKCE. Set the callback URL to your redirect URI. Required scopes: tweet.read, tweet.write, users.read, media.upload, offline.access.',
  },
  tiktok: {
    devUrl: 'https://developers.tiktok.com',
    devLabel: 'Create an app at developers.tiktok.com',
    idLabel: 'Client Key',
    secretLabel: 'Client Secret',
    instructions: 'Create a TikTok developer app and add Login Kit + Content Posting API products. Required scopes: user.info.basic, video.publish, video.upload.',
  },
  instagram: {
    devUrl: 'https://developers.facebook.com',
    devLabel: 'Create an app at developers.facebook.com',
    idLabel: 'App ID',
    secretLabel: 'App Secret',
    instructions: 'Create a Facebook app with Instagram Basic Display and Instagram Content Publishing products. Required permissions: instagram_basic, instagram_content_publish, pages_read_engagement, pages_show_list, business_management.',
  },
  snapchat: {
    devUrl: 'https://business.snapchat.com',
    devLabel: 'Create an app at business.snapchat.com',
    idLabel: 'Client ID',
    secretLabel: 'Client Secret',
    instructions: 'Create a Snap Kit app in the Snapchat Business Manager. Enable Login Kit and configure the Marketing API for Spotlight content posting. Required scope: snapchat-marketing-api.',
  },
};

export function SettingsPage() {
  const [connections, setConnections] = useState<ConnectionInfo[]>([]);
  const [credentials, setCredentials] = useState<CredentialStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [credentialsLoading, setCredentialsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [disconnecting, setDisconnecting] = useState<string | null>(null);
  const [expandedPlatform, setExpandedPlatform] = useState<string | null>(null);
  const [saving, setSaving] = useState<string | null>(null);
  const [clearing, setClearing] = useState<string | null>(null);
  const [credentialInputs, setCredentialInputs] = useState<Record<string, { clientId: string; clientSecret: string }>>({});
  const [credentialSuccess, setCredentialSuccess] = useState<string | null>(null);

  // API Keys state
  const [kiloConfigured, setKiloConfigured] = useState(false);
  const [kiloMaskedKey, setKiloMaskedKey] = useState<string | undefined>();
  const [kiloKeyInput, setKiloKeyInput] = useState('');
  const [kiloLoading, setKiloLoading] = useState(true);
  const [kiloSaving, setKiloSaving] = useState(false);
  const [kiloError, setKiloError] = useState<string | null>(null);
  const [kiloSuccess, setKiloSuccess] = useState(false);
  const [kiloShowInput, setKiloShowInput] = useState(false);
  const [showKeyValue, setShowKeyValue] = useState(false);

  // Captions.ai API Key state
  const [captionsAiConfigured, setCaptionsAiConfigured] = useState(false);
  const [captionsAiMaskedKey, setCaptionsAiMaskedKey] = useState<string | undefined>();
  const [captionsAiKeyInput, setCaptionsAiKeyInput] = useState('');
  const [captionsAiSaving, setCaptionsAiSaving] = useState(false);
  const [captionsAiError, setCaptionsAiError] = useState<string | null>(null);
  const [captionsAiSuccess, setCaptionsAiSuccess] = useState(false);
  const [captionsAiShowInput, setCaptionsAiShowInput] = useState(false);
  const [showCaptionsAiKeyValue, setShowCaptionsAiKeyValue] = useState(false);

  // Caption Engine Preferences state
  const [captionEngine, setCaptionEngine] = useState<'captions_ai'>('captions_ai');
  const [captionTemplateId, setCaptionTemplateId] = useState('ctpl_DxflLOnuKkb198FNdI9E');
  const { data: captionTemplates, loading: captionTemplatesLoading, error: captionTemplatesError } = useCaptionTemplates();
  const [prefsLoading, setPrefsLoading] = useState(true);
  const [prefsSaving, setPrefsSaving] = useState(false);
  const [prefsSuccess, setPrefsSuccess] = useState(false);
  const [prefsError, setPrefsError] = useState<string | null>(null);

  const fetchConnections = useCallback(async () => {
    try {
      setError(null);
      const result = await api.getConnections();
      setConnections(result.connections);
    } catch (err) {
      console.error('Failed to fetch connections:', err);
      setError('Failed to load connection status. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCredentials = useCallback(async () => {
    try {
      const result = await api.getCredentialsStatus();
      setCredentials(result.platforms);
    } catch (err) {
      console.error('Failed to fetch credentials status:', err);
    } finally {
      setCredentialsLoading(false);
    }
  }, []);

  const fetchSecretsStatus = useCallback(async () => {
    setKiloLoading(true);
    try {
      const result = await api.getSecretsStatus();
      setKiloConfigured(result.kilo.configured);
      setKiloMaskedKey(result.kilo.maskedKey);
      // Captions.ai key status
      if (result.captions_ai) {
        setCaptionsAiConfigured(result.captions_ai.configured);
        setCaptionsAiMaskedKey(result.captions_ai.maskedKey);
      }
    } catch (err) {
      console.error('Failed to fetch secrets status:', err);
    } finally {
      setKiloLoading(false);
    }
  }, []);

  const fetchPreferences = useCallback(async () => {
    setPrefsLoading(true);
    try {
      const result = await api.getPreferences();
      setCaptionEngine('captions_ai');
      setCaptionTemplateId(result.captionTemplateId);
    } catch (err) {
      console.error('Failed to fetch preferences:', err);
    } finally {
      setPrefsLoading(false);
    }
  }, []);

  const handleSaveKiloKey = async () => {
    if (!kiloKeyInput.trim()) return;
    setKiloSaving(true);
    setKiloError(null);
    setKiloSuccess(false);
    try {
      const result = await api.updateKiloKey(kiloKeyInput.trim());
      setKiloConfigured(result.configured);
      setKiloMaskedKey(result.maskedKey);
      setKiloKeyInput('');
      setKiloShowInput(false);
      setKiloSuccess(true);
      setTimeout(() => setKiloSuccess(false), 3000);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to save API key';
      setKiloError(message);
    } finally {
      setKiloSaving(false);
    }
  };

  const handleSaveCaptionsAiKey = async () => {
    if (!captionsAiKeyInput.trim()) return;
    setCaptionsAiSaving(true);
    setCaptionsAiError(null);
    setCaptionsAiSuccess(false);
    try {
      const result = await api.updateCaptionsAiKey(captionsAiKeyInput.trim());
      setCaptionsAiConfigured(result.configured);
      setCaptionsAiMaskedKey(result.maskedKey);
      setCaptionsAiKeyInput('');
      setCaptionsAiShowInput(false);
      setCaptionsAiSuccess(true);
      setTimeout(() => setCaptionsAiSuccess(false), 3000);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to save API key';
      setCaptionsAiError(message);
    } finally {
      setCaptionsAiSaving(false);
    }
  };

  const handleSavePreferences = async () => {
    setPrefsSaving(true);
    setPrefsError(null);
    setPrefsSuccess(false);
    try {
      await api.updatePreferences({
        captionEngine: 'captions_ai',
        captionTemplateId,
      });
      setPrefsSuccess(true);
      setTimeout(() => setPrefsSuccess(false), 3000);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to save preferences';
      setPrefsError(message);
    } finally {
      setPrefsSaving(false);
    }
  };

  useEffect(() => {
    fetchConnections();
    fetchCredentials();
    fetchSecretsStatus();
    fetchPreferences();
  }, [fetchConnections, fetchCredentials, fetchSecretsStatus, fetchPreferences]);

  // Detect OAuth callback redirect (e.g., ?youtube=connected)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    let shouldRefresh = false;

    for (const platform of OAUTH_PLATFORMS) {
      if (params.has(platform) || params.get('connected') === platform || params.get('platform') === platform) {
        shouldRefresh = true;
        break;
      }
    }

    if (params.has('success') || params.has('error') || params.has('status')) {
      shouldRefresh = true;
    }

    if (shouldRefresh) {
      window.history.replaceState({}, '', window.location.pathname);
      fetchConnections();
    }
  }, [fetchConnections]);

  const isCredentialsConfigured = (platform: string): boolean => {
    const cred = credentials.find((c) => c.platform === platform);
    return cred?.configured ?? false;
  };

  const handleConnect = (platform: string) => {
    const connectUrl = `${config.apiUrl}/platforms/${platform}/connect`;
    const popup = window.open(connectUrl, `connect_${platform}`, 'width=600,height=700,scrollbars=yes');

    if (popup) {
      const checkClosed = setInterval(() => {
        if (popup.closed) {
          clearInterval(checkClosed);
          fetchConnections();
        }
      }, 1000);
      setTimeout(() => clearInterval(checkClosed), 300000);
    }
  };

  const handleDisconnect = async (platform: string) => {
    if (!confirm(`Are you sure you want to disconnect ${PLATFORM_INFO[platform as Platform]?.name || platform}?`)) {
      return;
    }

    setDisconnecting(platform);
    try {
      await api.disconnectPlatform(platform);
      await fetchConnections();
    } catch (err) {
      console.error(`Failed to disconnect ${platform}:`, err);
      setError(`Failed to disconnect ${platform}. Please try again.`);
    } finally {
      setDisconnecting(null);
    }
  };

  const handleSaveCredentials = async (platform: string) => {
    const input = credentialInputs[platform];
    if (!input?.clientId || !input?.clientSecret) {
      return;
    }

    setSaving(platform);
    setCredentialSuccess(null);
    try {
      await api.updateCredentials(platform, input.clientId, input.clientSecret);
      await fetchCredentials();
      setCredentialInputs((prev) => ({
        ...prev,
        [platform]: { clientId: '', clientSecret: '' },
      }));
      setCredentialSuccess(platform);
      setTimeout(() => setCredentialSuccess(null), 3000);
    } catch (err) {
      console.error(`Failed to save credentials for ${platform}:`, err);
      setError(`Failed to save credentials for ${platform}. Please try again.`);
    } finally {
      setSaving(null);
    }
  };

  const handleClearCredentials = async (platform: string) => {
    if (!confirm(`Are you sure you want to clear credentials for ${PLATFORM_INFO[platform as Platform]?.name || platform}? This will prevent OAuth connections from working.`)) {
      return;
    }

    setClearing(platform);
    try {
      await api.clearCredentials(platform);
      await fetchCredentials();
    } catch (err) {
      console.error(`Failed to clear credentials for ${platform}:`, err);
      setError(`Failed to clear credentials for ${platform}. Please try again.`);
    } finally {
      setClearing(null);
    }
  };

  const getConnectionForPlatform = (platform: string): ConnectionInfo | undefined => {
    return connections.find((c) => c.platform === platform);
  };

  const getCredentialForPlatform = (platform: string): CredentialStatus | undefined => {
    return credentials.find((c) => c.platform === platform);
  };

  const formatDate = (dateStr?: string): string => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    } catch {
      return '';
    }
  };

  const updateCredentialInput = (platform: string, field: 'clientId' | 'clientSecret', value: string) => {
    setCredentialInputs((prev) => ({
      ...prev,
      [platform]: {
        ...(prev[platform] || { clientId: '', clientSecret: '' }),
        [field]: value,
      },
    }));
  };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <p className="text-sm text-gray-500">
          Configure your social media manager preferences
        </p>
      </div>

      {/* API Keys */}
      <section className="card">
        <div className="border-b border-gray-100 px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
                <Shield className="h-5 w-5 text-gray-400" />
                API Keys
              </h2>
              <p className="text-sm text-gray-500">
                Configure API keys for AI-powered processing
              </p>
            </div>
            <button
              onClick={() => { setKiloLoading(true); fetchSecretsStatus(); }}
              className="btn-secondary flex items-center gap-1.5 text-xs"
              disabled={kiloLoading}
            >
              <RefreshCw className={`h-3.5 w-3.5 ${kiloLoading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        <div className="px-6 py-4 space-y-4">
          {/* Success message */}
          {kiloSuccess && (
            <div className="flex items-center gap-2 rounded-lg bg-green-50 px-4 py-3 text-sm text-green-700">
              <CheckCircle2 className="h-4 w-4 shrink-0" />
              Kilo AI API key saved successfully!
            </div>
          )}

          {/* Error message */}
          {kiloError && (
            <div className="flex items-center gap-2 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              {kiloError}
            </div>
          )}

          {/* Kilo AI API Key */}
          <div className="flex items-start gap-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-orange-100 shrink-0">
              <span className="text-lg">🧠</span>
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <p className="text-sm font-medium text-gray-900">Kilo AI API Key</p>
                {kiloLoading ? (
                  <div className="flex items-center gap-1.5">
                    <Loader2 className="h-3 w-3 animate-spin text-gray-400" />
                    <span className="text-xs text-gray-400">Checking...</span>
                  </div>
                ) : kiloConfigured ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                    <CheckCircle2 className="h-3 w-3" />
                    Configured
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
                    <AlertTriangle className="h-3 w-3" />
                    Not Configured
                  </span>
                )}
              </div>

              {/* Current key display (when configured) */}
              {kiloConfigured && !kiloShowInput && (
                <div className="mt-2 flex items-center gap-2">
                  <code className="rounded-md bg-gray-100 px-3 py-1.5 text-sm font-mono text-gray-600">
                    {kiloMaskedKey || 'kilo_••••••••'}
                  </code>
                  <button
                    onClick={() => setKiloShowInput(true)}
                    className="btn-secondary text-xs"
                  >
                    Update
                  </button>
                </div>
              )}

              {/* Input field (when not configured, or updating) */}
              {(!kiloConfigured || kiloShowInput) && (
                <div className="mt-2 space-y-2">
                  <div className="relative">
                    <input
                      type={showKeyValue ? 'text' : 'password'}
                      value={kiloKeyInput}
                      onChange={(e) => setKiloKeyInput(e.target.value)}
                      placeholder="kilo_api03-..."
                      className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 pr-10 text-sm text-gray-900 placeholder-gray-400 font-mono focus:border-brand-green focus:outline-none focus:ring-1 focus:ring-brand-green"
                    />
                    <button
                      type="button"
                      onClick={() => setShowKeyValue(!showKeyValue)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showKeyValue ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={handleSaveKiloKey}
                      disabled={kiloSaving || !kiloKeyInput.trim()}
                      className="btn-primary flex items-center gap-1.5 text-xs"
                    >
                      {kiloSaving ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <Save className="h-3 w-3" />
                      )}
                      Save Key
                    </button>
                    {kiloShowInput && (
                      <button
                        onClick={() => {
                          setKiloShowInput(false);
                          setKiloKeyInput('');
                          setKiloError(null);
                        }}
                        className="btn-secondary text-xs"
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Help text */}
              <div className="mt-3 rounded-lg bg-gray-50 px-4 py-3">
                <div className="flex items-start gap-2">
                  <HelpCircle className="h-4 w-4 text-gray-400 mt-0.5 shrink-0" />
                  <div className="text-xs text-gray-600 space-y-1">
                    <p>
                      Required for AI-powered clip extraction and draft generation. One key for all models. Get yours at{' '}
                      <a
                        href="https://kilo.ai"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-brand-green hover:underline font-medium"
                      >
                        kilo.ai
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-gray-100" />

          {/* Captions.ai API Key */}
          {captionsAiSuccess && (
            <div className="flex items-center gap-2 rounded-lg bg-green-50 px-4 py-3 text-sm text-green-700">
              <CheckCircle2 className="h-4 w-4 shrink-0" />
              Captions.ai API key saved successfully!
            </div>
          )}

          {captionsAiError && (
            <div className="flex items-center gap-2 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              {captionsAiError}
            </div>
          )}

          <div className="flex items-start gap-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-purple-100 shrink-0">
              <span className="text-lg">✨</span>
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <p className="text-sm font-medium text-gray-900">Captions.ai API Key</p>
                {kiloLoading ? (
                  <div className="flex items-center gap-1.5">
                    <Loader2 className="h-3 w-3 animate-spin text-gray-400" />
                    <span className="text-xs text-gray-400">Checking...</span>
                  </div>
                ) : captionsAiConfigured ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                    <CheckCircle2 className="h-3 w-3" />
                    Configured
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500">
                    Optional
                  </span>
                )}
              </div>

              {/* Current key display (when configured) */}
              {captionsAiConfigured && !captionsAiShowInput && (
                <div className="mt-2 flex items-center gap-2">
                  <code className="rounded-md bg-gray-100 px-3 py-1.5 text-sm font-mono text-gray-600">
                    {captionsAiMaskedKey || '••••••••'}
                  </code>
                  <button
                    onClick={() => setCaptionsAiShowInput(true)}
                    className="btn-secondary text-xs"
                  >
                    Update
                  </button>
                </div>
              )}

              {/* Input field (when not configured, or updating) */}
              {(!captionsAiConfigured || captionsAiShowInput) && (
                <div className="mt-2 space-y-2">
                  <div className="relative">
                    <input
                      type={showCaptionsAiKeyValue ? 'text' : 'password'}
                      value={captionsAiKeyInput}
                      onChange={(e) => setCaptionsAiKeyInput(e.target.value)}
                      placeholder="Enter Captions.ai API key..."
                      className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 pr-10 text-sm text-gray-900 placeholder-gray-400 font-mono focus:border-brand-green focus:outline-none focus:ring-1 focus:ring-brand-green"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCaptionsAiKeyValue(!showCaptionsAiKeyValue)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showCaptionsAiKeyValue ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={handleSaveCaptionsAiKey}
                      disabled={captionsAiSaving || !captionsAiKeyInput.trim()}
                      className="btn-primary flex items-center gap-1.5 text-xs"
                    >
                      {captionsAiSaving ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <Save className="h-3 w-3" />
                      )}
                      Save Key
                    </button>
                    {captionsAiShowInput && (
                      <button
                        onClick={() => {
                          setCaptionsAiShowInput(false);
                          setCaptionsAiKeyInput('');
                          setCaptionsAiError(null);
                        }}
                        className="btn-secondary text-xs"
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Help text */}
              <div className="mt-3 rounded-lg bg-gray-50 px-4 py-3">
                <div className="flex items-start gap-2">
                  <HelpCircle className="h-4 w-4 text-gray-400 mt-0.5 shrink-0" />
                  <div className="text-xs text-gray-600 space-y-1">
                    <p>
                      Required for video captioning. Videos now use Captions.ai as the only live caption engine. Get a key at{' '}
                      <a
                        href="https://www.captions.ai"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-brand-green hover:underline font-medium"
                      >
                        captions.ai
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Caption Engine Selector */}
      <section className="card">
        <div className="border-b border-gray-100 px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
                <Subtitles className="h-5 w-5 text-gray-400" />
                Caption Engine
              </h2>
              <p className="text-sm text-gray-500">
                Captions.ai is now the required live caption engine for video clips
              </p>
            </div>
            {prefsLoading && (
              <div className="flex items-center gap-1.5">
                <Loader2 className="h-3.5 w-3.5 animate-spin text-gray-400" />
                <span className="text-xs text-gray-400">Loading...</span>
              </div>
            )}
          </div>
        </div>

        <div className="px-6 py-4 space-y-4">
          {/* Success message */}
          {prefsSuccess && (
            <div className="flex items-center gap-2 rounded-lg bg-green-50 px-4 py-3 text-sm text-green-700">
              <CheckCircle2 className="h-4 w-4 shrink-0" />
              Caption engine preference saved!
            </div>
          )}

          {/* Error message */}
          {prefsError && (
            <div className="flex items-center gap-2 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              {prefsError}
            </div>
          )}

          {/* Captions.ai Option */}
          <label
            className={`block cursor-pointer rounded-xl border-2 p-4 transition-all ${
              captionEngine === 'captions_ai'
                ? 'border-brand-green bg-green-50/50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <div className="flex items-start gap-3">
              <input
                type="radio"
                name="captionEngine"
                value="captions_ai"
                checked={captionEngine === 'captions_ai'}
                onChange={() => setCaptionEngine('captions_ai')}
                className="mt-1 h-4 w-4 border-gray-300 text-brand-green focus:ring-brand-green"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-purple-500" />
                  <span className="text-sm font-semibold text-gray-900">Captions.ai</span>
                  <span className="inline-flex items-center rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-700">
                    Recommended
                  </span>
                </div>
                <p className="mt-1 text-xs text-gray-600">
                  Professional animated captions via the Mirage API. This is the only live caption engine now. Mirage currently documents 9:16 videos under 5 minutes and 50 MB.
                </p>

                {/* Warning if API key not configured */}
                {captionEngine === 'captions_ai' && !captionsAiConfigured && (
                  <div className="mt-2 flex items-center gap-2 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700">
                    <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                    Configure your Captions.ai API key above first — captioning will fail without it
                  </div>
                )}

                {/* Template selector (only when Captions.ai is selected) */}
                {captionEngine === 'captions_ai' && (
                  <div className="mt-3">
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Caption Template
                    </label>
                    <select
                      value={captionTemplateId}
                      onChange={(e) => setCaptionTemplateId(e.target.value)}
                      disabled={captionTemplatesLoading || !captionTemplates?.length}
                      className="w-full max-w-xs rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 focus:border-brand-green focus:outline-none focus:ring-1 focus:ring-brand-green disabled:cursor-not-allowed disabled:bg-gray-100"
                    >
                      {captionTemplatesLoading && <option value="">Loading templates…</option>}
                      {!captionTemplatesLoading && (!captionTemplates || captionTemplates.length === 0) && (
                        <option value="">No templates available</option>
                      )}
                      {captionTemplates?.map((tmpl) => (
                        <option key={tmpl.id} value={tmpl.id}>
                          {tmpl.name}
                        </option>
                      ))}
                    </select>
                    {captionTemplatesError && (
                      <p className="mt-1 text-xs text-red-600">
                        Couldn't load templates from Captions.ai: {captionTemplatesError}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </label>

          {/* Save button */}
          <div className="pt-2">
            <button
              onClick={handleSavePreferences}
              disabled={prefsSaving || prefsLoading}
              className="btn-primary flex items-center gap-1.5"
            >
              {prefsSaving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              Save Caption Preference
            </button>
          </div>
        </div>
      </section>

      {/* Platform Credentials */}
      <section className="card">
        <div className="border-b border-gray-100 px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
                <Key className="h-5 w-5 text-gray-400" />
                Platform Credentials
              </h2>
              <p className="text-sm text-gray-500">
                Configure OAuth client IDs and secrets for each platform
              </p>
            </div>
            <button
              onClick={() => { setCredentialsLoading(true); fetchCredentials(); }}
              className="btn-secondary flex items-center gap-1.5 text-xs"
              disabled={credentialsLoading}
            >
              <RefreshCw className={`h-3.5 w-3.5 ${credentialsLoading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        <div className="divide-y divide-gray-50">
          {OAUTH_PLATFORMS.map((platform) => {
            const info = PLATFORM_INFO[platform] || { name: platform, icon: "📱", color: "bg-gray-500" };
            const cred = getCredentialForPlatform(platform);
            const devInfo = PLATFORM_DEV_INFO[platform];
            const isExpanded = expandedPlatform === platform;
            const isConfigured = cred?.configured ?? false;
            const isSaving = saving === platform;
            const isClearing = clearing === platform;
            const input = credentialInputs[platform] || { clientId: '', clientSecret: '' };
            const showSuccess = credentialSuccess === platform;

            return (
              <div key={platform} className="px-6">
                {/* Collapsible Header */}
                <button
                  className="flex w-full items-center justify-between py-4 text-left"
                  onClick={() => setExpandedPlatform(isExpanded ? null : platform)}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{info.icon}</span>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{info.name}</p>
                      {credentialsLoading ? (
                        <div className="flex items-center gap-1.5 text-xs text-gray-400">
                          <Loader2 className="h-3 w-3 animate-spin" />
                          Checking...
                        </div>
                      ) : isConfigured ? (
                        <div className="flex items-center gap-1.5">
                          <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
                          <span className="text-xs text-green-700">Configured</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1.5">
                          <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
                          <span className="text-xs text-amber-700">Not Configured</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {isConfigured ? (
                      <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                        ✅ Ready
                      </span>
                    ) : (
                      <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
                        ⚠️ Setup Required
                      </span>
                    )}
                    {isExpanded ? (
                      <ChevronDown className="h-4 w-4 text-gray-400" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-gray-400" />
                    )}
                  </div>
                </button>

                {/* Expanded Content */}
                {isExpanded && (
                  <div className="pb-4 pl-11 space-y-4">
                    {/* Success message */}
                    {showSuccess && (
                      <div className="flex items-center gap-2 rounded-lg bg-green-50 px-4 py-3 text-sm text-green-700">
                        <CheckCircle2 className="h-4 w-4 shrink-0" />
                        Credentials saved successfully!
                      </div>
                    )}

                    {/* Client ID */}
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        {devInfo?.idLabel || 'Client ID'}
                      </label>
                      <input
                        type="text"
                        value={input.clientId}
                        onChange={(e) => updateCredentialInput(platform, 'clientId', e.target.value)}
                        placeholder={cred?.clientIdSet ? '••••••••••••' : `Enter ${devInfo?.idLabel || 'Client ID'}`}
                        className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:border-brand-green focus:outline-none focus:ring-1 focus:ring-brand-green"
                      />
                      {cred?.clientIdSet && !input.clientId && (
                        <p className="mt-1 text-xs text-gray-400">Currently set — enter a new value to update</p>
                      )}
                    </div>

                    {/* Client Secret */}
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        {devInfo?.secretLabel || 'Client Secret'}
                      </label>
                      <input
                        type="password"
                        value={input.clientSecret}
                        onChange={(e) => updateCredentialInput(platform, 'clientSecret', e.target.value)}
                        placeholder={cred?.clientSecretSet ? '••••••••••••' : `Enter ${devInfo?.secretLabel || 'Client Secret'}`}
                        className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:border-brand-green focus:outline-none focus:ring-1 focus:ring-brand-green"
                      />
                      {cred?.clientSecretSet && !input.clientSecret && (
                        <p className="mt-1 text-xs text-gray-400">Currently set — enter a new value to update</p>
                      )}
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleSaveCredentials(platform)}
                        disabled={isSaving || !input.clientId || !input.clientSecret}
                        className="btn-primary flex items-center gap-1.5 text-xs"
                      >
                        {isSaving ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <Save className="h-3 w-3" />
                        )}
                        Save Credentials
                      </button>
                      {isConfigured && (
                        <button
                          onClick={() => handleClearCredentials(platform)}
                          disabled={isClearing}
                          className="btn-secondary flex items-center gap-1.5 text-xs text-red-600 hover:bg-red-50 hover:text-red-700"
                        >
                          {isClearing ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <Trash2 className="h-3 w-3" />
                          )}
                          Clear
                        </button>
                      )}
                    </div>

                    {/* Help / Developer Portal Link */}
                    {devInfo && (
                      <div className="rounded-lg bg-gray-50 px-4 py-3 space-y-2">
                        <div className="flex items-start gap-2">
                          <HelpCircle className="h-4 w-4 text-gray-400 mt-0.5 shrink-0" />
                          <div className="text-xs text-gray-600 space-y-1">
                            <p>{devInfo.instructions}</p>
                            <a
                              href={devInfo.devUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-brand-green hover:underline font-medium"
                            >
                              {devInfo.devLabel}
                              <ExternalLink className="h-3 w-3" />
                            </a>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* Platform Connections */}
      <section className="card">
        <div className="border-b border-gray-100 px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
                <ExternalLink className="h-5 w-5 text-gray-400" />
                Platform Connections
              </h2>
              <p className="text-sm text-gray-500">
                Connect your social media accounts for direct publishing
              </p>
            </div>
            <button
              onClick={() => { setLoading(true); fetchConnections(); }}
              className="btn-secondary flex items-center gap-1.5 text-xs"
              disabled={loading}
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {error && (
          <div className="mx-6 mt-4 flex items-center gap-2 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            {error}
          </div>
        )}

        <div className="divide-y divide-gray-50">
          {OAUTH_PLATFORMS.map((platform) => {
            const info = PLATFORM_INFO[platform] || { name: platform, icon: "📱", color: "bg-gray-500" };
            const conn = getConnectionForPlatform(platform);
            const isConnected = conn?.status === 'connected';
            const isExpired = conn?.status === 'expired';
            const isDisconnecting = disconnecting === platform;
            const hasCredentials = isCredentialsConfigured(platform);

            return (
              <div
                key={platform}
                className="flex items-center justify-between px-6 py-4"
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{info.icon}</span>
                  <div>
                    <p className="text-sm font-medium text-gray-900">{info.name}</p>
                    {loading ? (
                      <div className="flex items-center gap-1.5 text-xs text-gray-400">
                        <Loader2 className="h-3 w-3 animate-spin" />
                        Checking...
                      </div>
                    ) : isConnected ? (
                      <div className="flex items-center gap-1.5">
                        <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
                        <span className="text-xs text-green-700">
                          Connected{conn.accountName ? ` — ${conn.accountName}` : ''}
                        </span>
                        {conn.connectedAt && (
                          <span className="text-xs text-gray-400">
                            · {formatDate(conn.connectedAt)}
                          </span>
                        )}
                      </div>
                    ) : isExpired ? (
                      <div className="flex items-center gap-1.5">
                        <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
                        <span className="text-xs text-amber-700">
                          Token expired — reconnect needed
                        </span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1.5">
                        <XCircle className="h-3.5 w-3.5 text-gray-300" />
                        <span className="text-xs text-gray-500">Not connected</span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {isConnected ? (
                    <button
                      onClick={() => handleDisconnect(platform)}
                      disabled={isDisconnecting}
                      className="btn-secondary flex items-center gap-1.5 text-xs text-red-600 hover:bg-red-50 hover:text-red-700"
                    >
                      {isDisconnecting ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : null}
                      Disconnect
                    </button>
                  ) : (
                    <div className="relative group">
                      <button
                        onClick={() => handleConnect(platform)}
                        disabled={loading || !hasCredentials}
                        className={`btn-primary text-xs ${!hasCredentials ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        {isExpired ? 'Reconnect' : 'Connect'}
                      </button>
                      {!hasCredentials && (
                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block z-10">
                          <div className="rounded-lg bg-gray-900 px-3 py-2 text-xs text-white whitespace-nowrap shadow-lg">
                            Configure credentials first
                            <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1">
                              <div className="border-4 border-transparent border-t-gray-900" />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Notifications */}
      <section className="card">
        <div className="border-b border-gray-100 px-6 py-4">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
            <Bell className="h-5 w-5 text-gray-400" />
            Notifications
          </h2>
          <p className="text-sm text-gray-500">
            Configure how you receive notifications
          </p>
        </div>
        <div className="space-y-4 px-6 py-4">
          <label className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-900">
                Telegram Notifications
              </p>
              <p className="text-xs text-gray-500">
                Receive notifications via Telegram bot
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                Active
              </span>
            </div>
          </label>
          <label className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-900">
                New Upload Alerts
              </p>
              <p className="text-xs text-gray-500">
                Get notified when new content is uploaded
              </p>
            </div>
            <input
              type="checkbox"
              defaultChecked
              className="h-4 w-4 rounded border-gray-300 text-brand-green focus:ring-brand-green"
            />
          </label>
          <label className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-900">
                Draft Review Alerts
              </p>
              <p className="text-xs text-gray-500">
                Get notified when drafts are approved or rejected
              </p>
            </div>
            <input
              type="checkbox"
              defaultChecked
              className="h-4 w-4 rounded border-gray-300 text-brand-green focus:ring-brand-green"
            />
          </label>
        </div>
      </section>

      {/* About */}
      <section className="card px-6 py-4">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-green text-white font-bold text-sm">
            CCM
          </div>
          <div>
            <p className="text-sm font-semibold text-gray-900">
              Cream City Mortgage — Social Media Manager
            </p>
            <p className="text-xs text-gray-500">Version 1.0.0</p>
          </div>
        </div>
      </section>
    </div>
  );
}
